import { useContext, useEffect, useState } from "react";
import {
  getMappedFields,
  createMappedFields,
  updateMappedFields,
  deleteMappedFields,
} from "../services/configurations/fields";
import { MappedField } from "../interfaces";
import { AppContext } from "../context";

type UseType = "DATA" | "ACTIONS";
type ActionType = "CREATE" | "UPDATE" | "DELETE";

const handleFieldActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createMappedFields,
        fnMsg: t("MappedFields.mappedFieldsApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateMappedFields,
        fnMsg: t("MappedFields.mappedFieldsApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteMappedFields,
        fnMsg: t("MappedFields.mappedFieldsApiDeleteLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useMappedFields = (
  type: UseType,
  serviceApi?: string,
  dashboardOption?: boolean
) => {
  const {
    t,
    toast,
    appState: { selectedApp },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<MappedField[] | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || '0';

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const mappedFieldAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleFieldActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionState = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionState;
  };

  useEffect(() => {
    if (type === "DATA" && appid) {
      (async function () {
        await Promise.resolve(
          getMappedFields(appid, serviceApi, dashboardOption)
        )
          .then((response) => {
            const mappedFields: MappedField[] = response?.data;
            setData(mappedFields);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type, serviceApi, dashboardOption]);

  return {
    data,
    mappedFieldAction,
    reloadFn,
  };
};
